/* woolberry: near-instant internal navigation.
   ---------------------------------------------------------------------------
   Prefetches a same-origin page the moment the user signals intent — hovering
   a link (desktop, with a short intent delay), pressing the mouse button, or
   touching it (mobile). By the time the click actually fires, the next page is
   already in the browser cache, so it renders almost instantly.

   Self-contained (no external library / CDN, CSP-safe) and deliberately
   conservative:
     - same origin only; skips #anchors, downloads, target=_blank, rel
       external/nofollow
     - never prefetches cart / checkout / account / logout / add-to-cart or
       anything tagged data-cart-toggle / data-no-instant (avoids side effects
       and wasted requests)
     - honours Save-Data and slow (2G) connections
     - dedupes, and caps total prefetches per page so it can't hammer the
       server
   Add data-no-instant to any link you never want prefetched. */
(function () {
  'use strict';

  var conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
  if (!document.createElement('link').relList || !document.createElement('link').relList.supports) return;
  if (!document.createElement('link').relList.supports('prefetch')) return;

  var done = {};
  var count = 0;
  var MAX = 50; // safety cap per page view
  var HOVER_DELAY = 65; // ms of hover intent before prefetching
  var hoverTimer;
  var lastTouch = 0;

  function eligible(a) {
    if (!a || a.tagName !== 'A' || !a.href) return false;
    var url;
    try { url = new URL(a.href); } catch (e) { return false; }
    if (url.origin !== location.origin) return false;
    if (url.pathname === location.pathname && url.hash) return false; // in-page anchor
    if (a.hasAttribute('download')) return false;
    if (a.target && a.target !== '_self') return false;
    var rel = a.getAttribute('rel');
    if (rel && /(?:external|nofollow)/i.test(rel)) return false;
    if (a.hasAttribute('data-cart-toggle') || a.hasAttribute('data-no-instant')) return false;
    var p = url.pathname.toLowerCase();
    if (/\/(cart|checkout|account|challenge|orders|admin)(\/|$)/.test(p)) return false;
    if (/\/(logout|login)(\/|$)/.test(p)) return false;
    if (done[url.href]) return false;
    return true;
  }

  function prefetch(href) {
    if (done[href] || count >= MAX) return;
    done[href] = true;
    count++;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    document.head.appendChild(link);
  }

  function linkFrom(target) {
    return target && target.closest ? target.closest('a') : null;
  }

  // Desktop hover: prefetch after a short intent delay so merely sweeping the
  // cursor across links doesn't fetch all of them.
  document.addEventListener('mouseover', function (e) {
    if (performance.now() - lastTouch < 1100) return; // ignore hover synthesised after a tap
    var a = linkFrom(e.target);
    if (!eligible(a)) return;
    a.addEventListener('mouseout', cancelHover, { passive: true, once: true });
    hoverTimer = setTimeout(function () {
      prefetch(a.href);
      hoverTimer = null;
    }, HOVER_DELAY);
  }, { capture: true, passive: true });

  function cancelHover() {
    if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
  }

  // Mouse press fires ~100-150ms before click — prefetch with no delay.
  document.addEventListener('mousedown', function (e) {
    var a = linkFrom(e.target);
    if (eligible(a)) prefetch(a.href);
  }, { capture: true, passive: true });

  // Touch: touchstart lands well before the click on mobile.
  document.addEventListener('touchstart', function (e) {
    lastTouch = performance.now();
    var a = linkFrom(e.target);
    if (eligible(a)) prefetch(a.href);
  }, { capture: true, passive: true });
})();
